import React from "react";
import { Info, PencilLine, Plus, Rows3, TableProperties } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import TableRecordForm, { getPrimaryKeyField } from "./TableRecordForm";

function TableDetailsPanel({
  table,
  editorMode,
  selectedRow,
  onCreateRow,
  onCancelEdit,
  onSubmitRecord,
  onDeleteRecord,
  saving,
  error,
}) {
  if (!table) {
    return (
      <div className="flex h-full items-center justify-center px-6 text-center text-sm text-gray-500 dark:text-gray-400">
        Select a table to inspect its details.
      </div>
    );
  }

  const schema = table.schema;
  const primaryKey = getPrimaryKeyField(schema);

  if (editorMode === "create" || editorMode === "edit") {
    const title = editorMode === "create" ? "Create row" : "Edit row";
    const subtitle =
      editorMode === "create"
        ? `Add a new record to ${table.table}.`
        : `Editing ${primaryKey}: ${selectedRow?.[primaryKey]}`;

    return (
      <div className="flex h-full flex-col">
        <div className="border-b border-gray-200 px-5 py-4 dark:border-gray-800">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{subtitle}</p>
        </div>
        <div className="min-h-0 flex-1 px-5 py-5">
          <TableRecordForm
            tableName={table.table}
            schema={schema}
            initialData={editorMode === "edit" ? selectedRow : null}
            error={error}
            saving={saving}
            onSubmit={onSubmitRecord}
            onCancel={onCancelEdit}
            onDelete={editorMode === "edit" ? () => onDeleteRecord(selectedRow?.[primaryKey]) : null}
            compact={true}
          />
        </div>
      </div>
    );
  }

  const columnsCount = Object.keys(schema?.properties || {}).length;
  const requiredCount = schema?.required?.length || 0;
  const lastUpdated = table.last_updated ? new Date(table.last_updated).toLocaleString() : "N/A";

  return (
    <div className="flex h-full flex-col">
      <div className="border-b border-gray-200 px-5 py-4 dark:border-gray-800">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{table.table}</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {schema?.title || "Table details"}
            </p>
          </div>
          <Button onClick={onCreateRow}>
            <Plus className="mr-2 h-4 w-4" />
            Add
          </Button>
        </div>
      </div>

      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-5">
        <div className="grid gap-3 sm:grid-cols-2">
          <Card>
            <CardContent className="flex items-center gap-3 pt-5">
              <div className="rounded-xl bg-blue-50 p-3 text-blue-600 dark:bg-blue-950/50 dark:text-blue-300">
                <Rows3 className="h-5 w-5" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Rows</div>
                <div className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                  {Number(table.row_count || 0).toLocaleString()}
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-center gap-3 pt-5">
              <div className="rounded-xl bg-violet-50 p-3 text-violet-600 dark:bg-violet-950/50 dark:text-violet-300">
                <TableProperties className="h-5 w-5" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Columns</div>
                <div className="text-xl font-semibold text-gray-900 dark:text-gray-100">{columnsCount}</div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Overview</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-gray-600 dark:text-gray-300">
            <div className="flex items-center gap-2">
              <Info className="h-4 w-4 text-gray-400" />
              <span>{schema?.description || "No schema description available."}</span>
            </div>
            <div className="flex items-center gap-2">
              <PencilLine className="h-4 w-4 text-gray-400" />
              <span>
                Create mode: {schema?.create_disabled ? "disabled" : "enabled"} • Required fields: {requiredCount}
              </span>
            </div>
            <div>Primary key: {primaryKey || "Unknown"}</div>
            <div>Last update: {lastUpdated}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Quick field summary</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {Object.entries(schema?.properties || {})
              .slice(0, 8)
              .map(([fieldName, fieldSchema]) => (
                <div key={fieldName} className="rounded-lg border border-gray-200 px-3 py-2 dark:border-gray-800">
                  <div className="flex items-center justify-between gap-3">
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{fieldName}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {Array.isArray(fieldSchema.type) ? fieldSchema.type.join(" | ") : fieldSchema.type}
                    </div>
                  </div>
                  {fieldSchema.description && (
                    <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">{fieldSchema.description}</div>
                  )}
                </div>
              ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default TableDetailsPanel;
